import { tr } from '@/lib/i18n-core';
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileMinus, ListChecks, Lock, MessageSquare, Scale } from 'lucide-react';
import { customerCreditMinor, planAmountCreditNote, planLineCreditNote, type CreditableLine, type CreditNotePlan } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { money, newIdempotencyKey } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Field, FormSkeleton, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { toMinor } from './receipt-print';

export interface CreditableItem extends CreditableLine {
  creditableQty: number;
  lockedByApproval?: boolean;
}

export interface CreditNoteInvoice {
  id: string;
  number: string;
  context: string;
  netMinor: number;
  taxMinor: number;
  creditMinor: number;
  paidMinor: number;
  items: CreditableItem[];
}

interface CreditNotesOnBill {
  data: { status: string; netMinor: number; taxMinor: number; requestedBy: { id: string } | null; decidedBy: { id: string } | null }[];
  viewer: { canApprove: boolean; approvalLimitMinor: number; unlimited: boolean };
}

type Settlement = 'none' | 'refund' | 'keep_credit';

export function CreditNotePage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), `/billing/invoices/${invoiceId}`);

  const invoice = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => get<CreditNoteInvoice>(`/invoices/${invoiceId}`),
    enabled: Boolean(invoiceId),
  });

  if (invoice.isLoading) return <FormSkeleton />;
  if (!invoice.data) return <Alert variant="destructive">{tr('billing', 'that_invoice_could_not_be_opened')}</Alert>;
  return <CreditNoteForm key={invoice.data.id} invoice={invoice.data} returnTo={returnTo} />;
}

function CreditNoteForm({ invoice, returnTo }: { invoice: CreditNoteInvoice; returnTo: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const creditable = useMemo(
    () => (invoice.context === 'pharmacy' ? [] : invoice.items.filter((item) => item.creditableQty > 0)),
    [invoice.items, invoice.context],
  );
  const [mode, setMode] = useState<'lines' | 'amount'>(creditable.length > 0 ? 'lines' : 'amount');
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [settlement, setSettlement] = useState<Settlement>('refund');
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  const { user } = useAuth();
  const onBill = useQuery({
    queryKey: ['credit-notes', 'on-bill', invoice.id],
    queryFn: () => get<CreditNotesOnBill>(`/credit-notes${qs({ invoiceId: invoice.id, limit: 100 })}`),
    staleTime: 60_000,
  });
  const viewer = onBill.data?.viewer;
  const notesOnBill = onBill.data?.data;
  const waiting = useMemo(() => {
    const requested = (notesOnBill ?? []).filter((note) => note.status === 'requested');
    return {
      netMinor: requested.reduce((sum, note) => sum + note.netMinor, 0),
      taxMinor: requested.reduce((sum, note) => sum + note.taxMinor, 0),
    };
  }, [notesOnBill]);
  const appliedAloneMinor = (notesOnBill ?? [])
    .filter((note) => note.status === 'applied' && user && note.requestedBy?.id === user.id && note.decidedBy?.id === user.id)
    .reduce((sum, note) => sum + note.netMinor, 0);

  const requests = useMemo(
    () =>
      creditable
        .map((item) => ({ invoiceItemId: item.id, qty: Number(quantities[item.id] || 0) }))
        .filter((request) => request.qty > 0),
    [creditable, quantities],
  );

  const preview = useMemo<{ plan: CreditNotePlan | null; problem: string | null }>(() => {
    try {
      if (mode === 'lines') {
        if (requests.length === 0) return { plan: null, problem: null };
        return { plan: planLineCreditNote(invoice.items, requests), problem: null };
      }
      if (toMinor(amount) <= 0) return { plan: null, problem: null };
      return {
        plan: planAmountCreditNote({
          invoiceNetMinor: invoice.netMinor - waiting.netMinor,
          invoiceTaxMinor: Math.max(0, invoice.taxMinor - waiting.taxMinor),
          amountMinor: toMinor(amount),
          description: tr('billing', 'adjustment'),
        }),
        problem: null,
      };
    } catch (caught) {
      return { plan: null, problem: caught instanceof Error ? caught.message : 'That credit cannot be raised' };
    }
  }, [mode, requests, amount, invoice, waiting]);

  const plan = preview.plan;
  const overpaid =
    plan && invoice.creditMinor === 0 ? customerCreditMinor({ netMinor: invoice.netMinor - plan.netMinor, panelShareMinor: 0, paidMinor: invoice.paidMinor }) : 0;
  const within = plan && viewer ? viewer.unlimited || (viewer.canApprove && plan.netMinor + appliedAloneMinor <= viewer.approvalLimitMinor) : false;
  const effectiveSettlement: Settlement = overpaid > 0 ? settlement : 'none';

  const create = useMutation({
    mutationFn: () =>
      post<{ number: string; status: string }>(
        `/invoices/${invoice.id}/credit-notes`,
        {
          ...(mode === 'lines' ? { lines: requests } : { amountMinor: toMinor(amount) }),
          reason: reason.trim(),
          settlement: effectiveSettlement,
        },
        { idempotencyKey },
      ),
    onSuccess: (note) => {
      toast.success(
        note.status === 'applied' ? tr('billing', 'credit_note_x_issued', { number: note.number }) : tr('billing', 'credit_note_x_requested', { number: note.number }),
        note.status === 'applied' ? undefined : tr('billing', 'a_supervisor_will_decide_it'),
      );
      void queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['refunds'] });
      void queryClient.invalidateQueries({ queryKey: ['credit-notes'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const valid = Boolean(plan) && reason.trim().length >= 5;
  const dirty = !create.isSuccess && (requests.length > 0 || amount !== '' || reason !== '');

  return (
    <FormScreen
      title={tr('billing', 'credit_note_against_x', { number: invoice.number })}
      description={tr('billing', 'reduces_what_the_invoice_is_worth_the_original')}
      eyebrow={tr('billing', 'billing')}
      icon={FileMinus}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setError(null);
        create.mutate();
      }}
    >
      <FormSection title={tr('billing', 'what_to_credit')} icon={ListChecks} columns={1}>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant={mode === 'lines' ? 'default' : 'outline'} onClick={() => setMode('lines')} disabled={creditable.length === 0}>
            {tr('billing', 'credit_lines')}
          </Button>
          <Button type="button" size="sm" variant={mode === 'amount' ? 'default' : 'outline'} onClick={() => setMode('amount')}>
            {tr('billing', 'credit_an_amount')}
          </Button>
        </div>

        {mode === 'lines' ? (
          <div className="space-y-2">
            {creditable.map((item) => {
              const qty = Number(quantities[item.id] || 0);
              let share: string | null = null;
              if (qty > 0) {
                try {
                  share = money(planLineCreditNote(invoice.items, [{ invoiceItemId: item.id, qty }]).netMinor);
                } catch {
                  share = null;
                }
              }
              return (
                <div key={item.id} className="grid items-center gap-2 rounded-md border p-2 sm:grid-cols-12">
                  <div className="sm:col-span-6">
                    <p className="text-sm font-medium">{item.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {tr('billing', 'billed')} {item.qty} · {money(item.netMinor)} {tr('billing', 'up_to')} {item.creditableQty} {tr('billing', 'can_still_be_credited')}
                    </p>
                    {item.lockedByApproval && (
                      <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-warning">
                        <Lock className="h-3 w-3" aria-hidden /> {tr('billing', 'nx_locked_by_approval')}
                      </p>
                    )}
                  </div>
                  <Input
                    className="sm:col-span-3"
                    type="number"
                    min={0}
                    max={item.creditableQty}
                    disabled={item.lockedByApproval === true}
                    step="any"
                    placeholder={tr('billing', 'quantity')}
                    value={quantities[item.id] ?? ''}
                    onChange={(e) => setQuantities((current) => ({ ...current, [item.id]: e.target.value }))}
                  />
                  <p className="tabular text-end text-sm sm:col-span-3">{share ? `− ${share}` : ''}</p>
                </div>
              );
            })}
            {creditable.length === 0 && (
              <Alert>
                {invoice.context === 'pharmacy' ? tr('billing', 'medicine_the_customer_brings_back_goes_through') : tr('billing', 'every_line_on_this_invoice_has_already_been')}
              </Alert>
            )}
          </div>
        ) : (
          <Field label={tr('billing', 'amount_to_credit')} required hint={tr('billing', 'up_to_x_tax_comes_off_in_the_same_proportion', { netMinor: money(invoice.netMinor) })}>
            <Input type="number" step="0.01" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} className="tabular text-lg" />
          </Field>
        )}
      </FormSection>

      <FormSection title={tr('billing', 'reason')} icon={MessageSquare} columns={1}>
        <Field label={tr('billing', 'reason')} required hint={tr('billing', 'at_least_five_characters_it_is_printed_on_the')}>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={400} />
        </Field>
      </FormSection>

      {plan && (
        <FormSection title={tr('billing', 'summary')} icon={Scale} columns={1}>
          <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
            <div className="flex justify-between">
              <span>{tr('billing', 'credit_total')}</span>
              <strong className="tabular">{money(plan.netMinor)}</strong>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>{tr('billing', 'of_which_tax')}</span>
              <span className="tabular">{money(plan.taxMinor)}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>{tr('billing', 'invoice_becomes')}</span>
              <span className="tabular">{money(invoice.netMinor - plan.netMinor)}</span>
            </div>
          </div>

          {overpaid > 0 && (
            <Field label={tr('billing', 'the_customer_has_paid_x_more_than_the_new_total', { overpaid: money(overpaid) })}>
              <ChoiceButtons<Settlement>
                layout="cards"
                value={settlement}
                onChange={setSettlement}
                options={[
                  { value: 'refund', label: tr('billing', 'refund_it_now_the_same_way_it_was_paid') },
                  { value: 'keep_credit', label: tr('billing', 'keep_it_as_credit_on_this_invoice') },
                ]}
              />
            </Field>
          )}
          {invoice.creditMinor > 0 && invoice.paidMinor > 0 && <Alert>{tr('billing', 'the_panel_share_moves_with_the_new_total_if_the')}</Alert>}

          {viewer && (
            <Alert variant={within ? 'success' : 'warning'}>
              {within
                ? tr('billing', 'within_your_approval_limit_so_it_takes_effect')
                : viewer.canApprove
                  ? tr('billing', 'above_your_approval_limit_of_x_so_it_waits_for', { approvalLimitMinor: money(viewer.approvalLimitMinor) })
                  : tr('billing', 'you_cannot_approve_credit_notes_yourself_so_it')}
            </Alert>
          )}
        </FormSection>
      )}

      {preview.problem && <Alert variant="destructive">{preview.problem}</Alert>}
      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={within ? tr('billing', 'issue_credit_note') : tr('billing', 'ask_for_approval')}
        loading={create.isPending}
        incomplete={!valid}
        cancelTo={returnTo}
      />
    </FormScreen>
  );
}
